import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "store/services/authService";
import { isEmpty, throttle, times } from "lodash";
import { parseAutoLogoutLabel } from "helpers/common";

export const ACTIVITY_KEY = "user-activity";
export const LOGOUT_KEY = "user-logout";
export const DISABLE_AUTO_LOGOUT_KEY = "disable-auto-logout";
const DEFAULT_AUTO_LOGOUT_DURATION = 172800;

const useInactivityTimer = () => {
  const userInfo = useSelector((state) => state.Auth.user);
  const accountSettings = useSelector((state) => state.AccountSetting.accountSettingDetail_);
  const [isInactive, setIsInactive] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const intervalRef = useRef(null);
  const dispatch = useDispatch();

  const timeoutSeconds = useMemo(() => {
    if(isEmpty(accountSettings)) {
      return null;
    }

    return Number(accountSettings?.auto_logout_duration) || DEFAULT_AUTO_LOGOUT_DURATION;
  }, [accountSettings]);

  const broadcastActivity = () => {
    localStorage.setItem(ACTIVITY_KEY, Date.now().toString());
  };

  const handleActivity = () => {
    if (!isInactive) {
      broadcastActivity();
    }
  };

  const logoutUser = async (e) => {
    if (e?.stopPropagation) e.stopPropagation();
    setLogoutLoading(true);
    try {
      await dispatch(logout());
      localStorage.setItem(LOGOUT_KEY, Date.now().toString());
    } catch (err) {
      console.error("Error in auto logout", err);
    } finally {
      setLogoutLoading(false);
      setIsInactive(false);
    }
  };

  const resetInactivity = () => {
    setIsInactive(false);
    broadcastActivity();
  };

  useEffect(() => {
    if (userInfo?.token) {
      broadcastActivity();
    }
  }, [userInfo?.token]);

  useEffect(() => {
    if (!userInfo?.token || !timeoutSeconds) return;

    // Skip inactivity timer if disable flag is set in localStorage
    if (sessionStorage.getItem(DISABLE_AUTO_LOGOUT_KEY) === "true") {
      console.log("Auto logout is disabled.");
      return;
    } else {
      console.log(`Auto logout in: ${parseAutoLogoutLabel(timeoutSeconds)}`);
    }

    if (!localStorage.getItem(ACTIVITY_KEY)) {
      broadcastActivity();
    }

    const checkInactivity = () => {
      const lastActivity = parseInt(localStorage.getItem(ACTIVITY_KEY), 10);
      if (!lastActivity) return;
      const now = Date.now();
      const elapsedSeconds = (now - lastActivity) / 1000;
      if (elapsedSeconds >= timeoutSeconds && !isInactive) {
        setIsInactive(true);
      }
    };

    intervalRef.current = setInterval(checkInactivity, 1000);

    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    const throttledHandleActivity = throttle(handleActivity, 1000);

    events.forEach((event) =>
      window.addEventListener(event, throttledHandleActivity)
    );

    const handleStorage = (e) => {
      if (e.key === ACTIVITY_KEY) {
        if (isInactive) setIsInactive(false);
      }
      if (e.key === LOGOUT_KEY) {
        dispatch(logout());
      }
    };

    window.addEventListener("storage", handleStorage);

    return () => {
      clearInterval(intervalRef.current);
      events.forEach((event) =>
        window.removeEventListener(event, throttledHandleActivity)
      );
      window.removeEventListener("storage", handleStorage);
      throttledHandleActivity.cancel();
    };
  }, [userInfo?.token, isInactive, timeoutSeconds]);

  return {
    isInactive,
    logoutLoading,
    logoutUser,
    resetInactivity,
    setInactiveManually: () => setIsInactive(true),
  };
};

export default useInactivityTimer;
